import { PersistenceLevel, Store } from '@/core/flux.service';
import {
  ScalingTableColumnDefinition,
  TableColumnDefinition,
  TableDefinitionColumnOptions,
} from '@/tableDefinitionEditor/tableDefinition.types';

/**
 * Base class store for the table definition editor
 * Table definition state is not stored in worksteps, so we separate the logic of what table is being looked at
 * here, which is stored in the workstep, while the table definition state is stored in the table definition store
 * which is updated when we receive messages from the server on the socket for table definitions.
 * This base class contains all handler functions, getters and state management that can be used in other editor stores
 */
export abstract class TableDefinitionEditorStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  initialize() {
    this.state = this.immutable({
      tableId: '',
      showConfigureSearch: false,
      columnToEdit: undefined,
      enableTreeCreation: false,
      selectedColumnOption: undefined,
      showSidePane: true,
      shortcutSourceColumn: undefined,
    });
  }

  get tableId(): string {
    return this.state.get('tableId');
  }

  get showConfigureSearch(): boolean {
    return this.state.get('showConfigureSearch');
  }

  get columnToEdit(): ScalingTableColumnDefinition | undefined {
    return this.state.get('columnToEdit');
  }

  get showColumnProperties(): boolean {
    return this.state.get('showColumnProperties');
  }

  get enableTreeCreation(): boolean {
    return this.state.get('enableTreeCreation');
  }

  get selectedColumnOption(): TableDefinitionColumnOptions | undefined {
    return this.state.get('selectedColumnOption');
  }

  get showSidePane(): boolean {
    return this.state.get('showSidePane');
  }

  get shortcutSourceColumn(): ScalingTableColumnDefinition | undefined {
    return this.state.get('shortcutSourceColumn');
  }

  protected readonly handlers = {};

  setTableId = (payload: { tableId: string }) => {
    this.state.set('tableId', payload.tableId);
  };

  setShowConfigureSearch = (payload: { showConfigureSearch: boolean }) => {
    this.state.set('showConfigureSearch', payload.showConfigureSearch);
  };

  setColumnToEdit = (payload: { columnToEdit: TableColumnDefinition | undefined }) => {
    this.state.set('columnToEdit', payload.columnToEdit);
  };

  setShowColumnProperties = (payload: { showColumnProperties: boolean }) => {
    this.state.set('showColumnProperties', payload.showColumnProperties);
  };

  setEnableTreeCreation = ({ enableTreeCreation }: { enableTreeCreation: boolean }) => {
    this.state.set('enableTreeCreation', enableTreeCreation);
  };

  setSelectedColumnOption = ({
    selectedColumnOption,
  }: {
    selectedColumnOption: TableDefinitionColumnOptions | undefined;
  }) => {
    this.state.set('selectedColumnOption', selectedColumnOption);
  };

  setShowSidePane = (payload: { showSidePane: boolean }) => {
    this.state.set('showSidePane', payload.showSidePane);
  };

  setShortcutSourceColumn = (payload: { shortcutSourceColumn: ScalingTableColumnDefinition | undefined }) => {
    this.state.set('shortcutSourceColumn', payload.shortcutSourceColumn);
  };

  reset = () => {
    this.initialize();
  };

  /**
   * Dehydrates the item by retrieving the current set parameters in view
   *
   * @returns An object with the state properties as JSON
   */
  dehydrate() {
    return this.state.serialize();
  }

  /**
   * Rehydrates item from dehydrated state
   *
   * @param dehydratedState - State object that should be restored
   */
  rehydrate(dehydratedState: Record<string, any>) {
    this.state.merge(dehydratedState);
  }
}
